import Box from '@mui/material/Box'
import Modal from '@mui/material/Modal'
import CloseIcon from '@mui/icons-material/Close'
import IconButton from '@mui/material/IconButton'
import styles from './styles.module.css'
import { IOrder } from '../../../../../app/entities/Order'
import { IOrderVehicle } from '../../../../../app/entities/OrderVehicle'

interface OrderDetailsModalProps {
  isOpen: boolean
  onClose: () => void
  order: IOrder
  orderVehicle: IOrderVehicle | null
}

export default function OrderDetailsModal({
  isOpen,
  onClose,
  order,
  orderVehicle,
}: OrderDetailsModalProps) {
  // Helper to format tire size
  const formatTireSize = (tire: any) => {
    if (!tire) return 'N/A'
    const width = tire.width?.toString() || ''
    const aspectRatio = tire.aspectRatio?.toString() || ''
    const diameter = tire.diameter?.toString() || ''
    if (!width && !aspectRatio && !diameter) return 'N/A'
    return `${width}/${aspectRatio}R${diameter}`
  }

  // Work-order identifier
  const workOrderId: string = order?.workOrderId?.toString() ?? 'N/A'

  // Order note
  const orderNote: string =
    (orderVehicle?.order?.note ?? order?.note ?? '').toString().trim() ||
    'No notes available'

  // Tire sizes and counts
  const tiresCombination = orderVehicle?.tiresCombination
  const frontTire = tiresCombination?.frontTire
  const rearTire = tiresCombination?.rearTire
  const frontTireSize = formatTireSize(frontTire)
  const rearTireSize = formatTireSize(rearTire)
  const frontTireCount =
    typeof tiresCombination?.frontCount === 'number'
      ? tiresCombination.frontCount
      : 'N/A'
  const rearTireCount =
    typeof tiresCombination?.rearCount === 'number'
      ? tiresCombination.rearCount
      : 'N/A'
  const totalTireCount =
    (typeof frontTireCount === 'number' ? frontTireCount : 0) +
    (typeof rearTireCount === 'number' ? rearTireCount : 0)

  // Cancellation info
  // Check for cancelled_onsite status in a case-insensitive way and handle both string formats
  const isCancelledOnsite = order?.status
    ? order.status.toLowerCase() === 'cancelled_onsite'
    : false

  // Check if we have any cancellation information from orderVehicle
  const hasCancellationInfo = Boolean(
    orderVehicle?.cosReason1 ||
      orderVehicle?.cosReason2 ||
      orderVehicle?.cosReason3 ||
      orderVehicle?.cosNote ||
      orderVehicle?.cosTireLocation,
  )

  const cosReason1 = orderVehicle?.cosReason1 || 'N/A'
  const cosReason2 = orderVehicle?.cosReason2 || 'N/A'
  const cosReason3 = orderVehicle?.cosReason3 || 'N/A'
  const cosNote = orderVehicle?.cosNote || 'No cancellation note available'
  const cosTireLocation = orderVehicle?.cosTireLocation || 'N/A'

  const hasCancellationDetails = isCancelledOnsite || hasCancellationInfo

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
    >
      <Box className={styles.orderDetailsModal}>
        <div className={styles.modalHeader}>
          <div className={styles.modalTitle}>Order Details</div>
          <IconButton
            onClick={onClose}
            className={styles.closeButton}
            aria-label='close'
          >
            <CloseIcon />
          </IconButton>
        </div>
        <div className={styles.modalContent}>
          <div className={styles.detailSection}>
            <div className={styles.sectionTitleEnhanced}>
              Work Order Information
            </div>
            <div className={styles.fieldValue}>
              <strong>Work Order ID:</strong> {workOrderId}
            </div>
          </div>
          <div className={styles.detailSection}>
            <div className={styles.sectionTitleEnhanced}>Order Note</div>
            {/* Force re-render with key based on content to make sure updates are reflected */}
            <div
              key={`note-${orderNote}`}
              className={styles.noteText}
            >
              {orderNote}
            </div>
          </div>

          {/* Enhanced tire information section */}
          <div className={styles.detailSection}>
            <div className={styles.sectionTitleEnhanced}>Tire Information</div>
            <div className={styles.tireInfo}>
              <div className={styles.tireInfoRow}>
                <div className={styles.fieldValue}>
                  <strong>Front Tire Size:</strong> {frontTireSize}
                </div>
                <div className={styles.tireCount}>
                  <strong>Count:</strong> {frontTireCount}
                </div>
              </div>
              <div className={styles.tireInfoRow}>
                <div className={styles.fieldValue}>
                  <strong>Rear Tire Size:</strong> {rearTireSize}
                </div>
                <div className={styles.tireCount}>
                  <strong>Count:</strong> {rearTireCount}
                </div>
              </div>
              <div className={styles.totalTires}>
                <strong>Total Tires:</strong> {totalTireCount}
              </div>
            </div>
          </div>

          {/* Add Cancellation Information section if applicable */}
          {hasCancellationDetails && (
            <div className={styles.detailSection}>
              <div className={styles.sectionTitleEnhanced}>
                Cancellation Information
              </div>
              {isCancelledOnsite && (
                <div className={styles.fieldValue}>
                  <strong>Status:</strong> Cancelled On Site
                </div>
              )}
              {orderVehicle?.cosReason1 && (
                <div className={styles.fieldValue}>
                  <strong>Primary Reason:</strong> {cosReason1}
                </div>
              )}
              {orderVehicle?.cosReason2 && (
                <div className={styles.fieldValue}>
                  <strong>Secondary Reason:</strong> {cosReason2}
                </div>
              )}
              {orderVehicle?.cosReason3 && (
                <div className={styles.fieldValue}>
                  <strong>Tertiary Reason:</strong> {cosReason3}
                </div>
              )}
              {orderVehicle?.cosTireLocation && (
                <div className={styles.fieldValue}>
                  <strong>Tire Location:</strong> {cosTireLocation}
                </div>
              )}
              {/* Always show cancellation note when there are cancellation details */}
              <div className={styles.fieldValue}>
                <strong>Cancellation Note:</strong>
              </div>
              <div className={styles.noteText}>{cosNote}</div>
            </div>
          )}
        </div>

        <div className={styles.modalActions}>
          <button
            className={styles.actionButton}
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </Box>
    </Modal>
  )
}
